"use client";

import { useEffect, useMemo, useState } from "react";
import NextLink from "next/link";

import {
  DEMO_CLAIM_DASHBOARD_PATH,
  DEMO_CLAIM_GO_TO_DASHBOARD_LABEL,
  DEMO_COUNTDOWN_TICK_MS,
  DEMO_ORGANIZATION_BANNER_LABEL,
  DEMO_ORGANIZATION_EXPIRED_MESSAGE,
  DEMO_ORGANIZATION_EXPIRED_TITLE,
  DEMO_ORGANIZATION_SIGN_OUT_LABEL,
  DEMO_ORGANIZATION_SIGN_OUT_PATH,
  DEMO_REVOKE_EXPIRED_TOKEN_PATH,
} from "@/constants/demo-organization";
import { useActiveOrganization } from "@/context/active-organization";
import { formatDemoTimeLeft } from "./format-demo-time-left";

export const DemoOrganizationChrome = ({ children }: { children: React.ReactNode }) => {
  const { activeOrganization } = useActiveOrganization();
  const demoExpiresAt = activeOrganization?.demoExpiresAt ?? null;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!demoExpiresAt) {
      return;
    }
    const id = window.setInterval(() => setNow(Date.now()), DEMO_COUNTDOWN_TICK_MS);
    return () => window.clearInterval(id);
  }, [demoExpiresAt]);

  const timeLeftMs = useMemo(() => {
    if (!demoExpiresAt) {
      return null;
    }
    return new Date(demoExpiresAt).getTime() - now;
  }, [demoExpiresAt, now]);

  const isExpired = timeLeftMs != null && timeLeftMs <= 0;

  useEffect(() => {
    if (!activeOrganization?.id || !demoExpiresAt || !isExpired) {
      return;
    }
    void fetch(DEMO_REVOKE_EXPIRED_TOKEN_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ organizationId: activeOrganization.id }),
    })
      .then((response) => {
        if (!response.ok) {
          console.error("DemoOrganizationChrome revoke-expired-token failed", {
            status: response.status,
            organizationId: activeOrganization.id,
          });
        }
      })
      .catch((error: unknown) => {
        console.error("DemoOrganizationChrome revoke-expired-token failed", {
          organizationId: activeOrganization.id,
          error,
        });
      });
  }, [activeOrganization?.id, demoExpiresAt, isExpired]);

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      {isExpired ? (
        <div className="z-30 shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-4 text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-50">
          <h2 className="text-heading-3 text-amber-950 dark:text-amber-50">
            {DEMO_ORGANIZATION_EXPIRED_TITLE}
          </h2>
          <p className="mt-2 text-paragraph-small-normal text-amber-900 dark:text-amber-100">
            {DEMO_ORGANIZATION_EXPIRED_MESSAGE}
          </p>
          <div className="mt-3 flex flex-wrap gap-4">
            <NextLink
              href={DEMO_CLAIM_DASHBOARD_PATH}
              className="text-sm font-medium underline underline-offset-2"
            >
              {DEMO_CLAIM_GO_TO_DASHBOARD_LABEL}
            </NextLink>
            <NextLink
              href={DEMO_ORGANIZATION_SIGN_OUT_PATH}
              className="text-sm font-medium underline underline-offset-2"
            >
              {DEMO_ORGANIZATION_SIGN_OUT_LABEL}
            </NextLink>
          </div>
        </div>
      ) : timeLeftMs != null ? (
        <div className="z-30 shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-100">
          <span className="font-semibold">{DEMO_ORGANIZATION_BANNER_LABEL}</span>
          <span className="mx-2">·</span>
          <span>{formatDemoTimeLeft(timeLeftMs)}</span>
        </div>
      ) : null}
      {/* Intentional: multi-org expired demos keep children interactive so the org
          switcher remains usable; proxy/API gates enforce access, not this banner. */}
      <div className="relative min-h-0 flex-1">{children}</div>
    </div>
  );
};
